import EmptyStateComponent from "@ch/components/EmptyState";
import EllipsesLoader from "@ch/components/Loaders/Ellipses";
import type { FC } from "react";
import type { TKemMessagePayload } from "../utils/types";
import KemChatMessageRow from "./KemChatMessageRow";

export interface KemChatPanelProps {
  combinedChatRef: (el: HTMLDivElement | null) => void;
  onScroll: () => void;
  showTopPaginationLoader: boolean;
  /** Full-height centered spinner only when there is nothing to show yet. */
  isCenterLoading: boolean;
  isEmpty: boolean;
  emptyTitle?: string;
  emptyInfo?: string;
  messages: TKemMessagePayload[];
  onKemBubbleImageLoad: (rowIndex: number) => void;
  setMessageAudioUrl?: (messageId: string, url: string) => void;
  handleMarkMessageVisible?: (messageId: string) => void;
}

const DEFAULT_EMPTY_TITLE = "No conversations yet";
const DEFAULT_EMPTY_INFO =
  "Type in the input box below to begin a conversation";

const KemChatPanel: FC<KemChatPanelProps> = ({
  combinedChatRef,
  onScroll,
  showTopPaginationLoader,
  isCenterLoading,
  isEmpty,
  emptyTitle = DEFAULT_EMPTY_TITLE,
  emptyInfo = DEFAULT_EMPTY_INFO,
  messages,
  onKemBubbleImageLoad,
  setMessageAudioUrl,
  handleMarkMessageVisible,
}) => (
  <div
    className="relative min-h-0 min-w-0 flex-1 overflow-y-auto"
    onScroll={onScroll}
    ref={combinedChatRef}
  >
    {showTopPaginationLoader && (
      <div aria-live="polite" className="mb-4 flex justify-center">
        <EllipsesLoader />
      </div>
    )}

    {isCenterLoading && isEmpty ? (
      <div className="flex h-full w-full min-w-0 items-center justify-center px-4 py-6 sm:px-5">
        <EllipsesLoader />
      </div>
    ) : isEmpty ? (
      <div className="flex h-full w-full min-w-0 items-center justify-center px-4 py-6 sm:px-5">
        <EmptyStateComponent info={emptyInfo} title={emptyTitle} />
      </div>
    ) : (
      <div className="flex min-w-0 flex-col">
        {messages.map((message, index) => (
          <KemChatMessageRow
            handleMarkMessageVisible={handleMarkMessageVisible}
            key={message.id ?? `kem-msg-${index}`}
            message={message}
            onKemBubbleImageLoad={onKemBubbleImageLoad}
            previousMessage={index > 0 ? messages[index - 1] : undefined}
            rowIndex={index}
            setMessageAudioUrl={setMessageAudioUrl}
          />
        ))}
      </div>
    )}
  </div>
);

export default KemChatPanel;
